import {
  useAssetListClustersQuery,
  useClusters,
} from '@cast/shared-cluster-context-data-access';

import { useCloudAssetIntegrationsQuery } from '@oneclick/hooks/queries/inventory/useCloudAssetIntegrationsQuery';

/**
 * True when the org already has agent clusters, inventory (snapshot) clusters,
 * or a cloud asset integration — i.e. they should not be forced through the
 * full "How would you like to connect?" onboarding chooser again.
 *
 * When a status query fails without usable connected evidence,
 * `isStatusUnknown` is true so callers can avoid treating the org as
 * confirmed-disconnected.
 */
export const useHasConnectedEnvironment = () => {
  const {
    clusters,
    isLoading: isClustersLoading,
    hasError: hasClustersError,
  } = useClusters();
  const {
    assetListClusters,
    isLoading: isAssetListLoading,
    error: assetListError,
  } = useAssetListClustersQuery();
  const {
    data: integrations,
    isLoading: isIntegrationsLoading,
    error: integrationsError,
  } = useCloudAssetIntegrationsQuery();

  const isLoading =
    isClustersLoading || isAssetListLoading || isIntegrationsLoading;

  const hasConnectedEnvironment =
    clusters.length > 0 ||
    (assetListClusters?.length ?? 0) > 0 ||
    (integrations?.length ?? 0) > 0;

  const hasStatusError =
    hasClustersError || Boolean(assetListError) || Boolean(integrationsError);

  const isStatusUnknown =
    !isLoading && hasStatusError && !hasConnectedEnvironment;

  return { isLoading, hasConnectedEnvironment, isStatusUnknown };
};
